import { emitAppEvent } from '@/lib/appEvents';
import {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@heroui/react';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { toast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { uploadFile } from '@/lib/uploader';
import type { Message } from '@/types/api';
import {
  clearDraft,
  flushDraft,
  getDraft,
  setDraft as persistDraft,
} from '@/lib/drafts';
import { PollCreatorModal } from '@/components/PollCreatorModal';
import { TaskCreatorModal } from '@/components/TaskCreatorModal';
import { type RecordingResult } from '@/lib/voiceRecorder';
import { useHoldToRecord } from '@/components/contentPane/useHoldToRecord';
import {
  buildOptimisticMessage,
  buildSendPayload,
  canSendMessage,
  collectReadyAttachments,
  hasUploadsInFlight,
  planSendBatches,
} from '@/components/contentPane/composerMessage';
import {
  detectSlashQuery,
  insertMention,
  slashPickText,
} from '@/components/contentPane/composerText';
import { useComposerUploads } from '@/components/contentPane/useComposerUploads';
import {
  mergeFailedParts,
  mergeRestoredDraft,
  retryNonce,
} from '@/components/contentPane/composerSend';
import { sendQueue, useFailedSendHandBack } from '@/components/contentPane/composerQueue';
import { generateClientNonce as newNonce } from '@bunker/shared/api';
import {
  wrapBold,
  wrapCode,
  wrapItalic,
  wrapLink,
  wrapQuote,
  wrapSpoiler,
  wrapStrike,
  wrapUnderline,
  type WrapResult,
} from '@/lib/formatWrap';
import { parseSlashCommand, rankSlashCommands, SLASH_COMMANDS } from '@/lib/slashCommands';
import { detectMentionQuery, rankMentions } from '@/lib/mentionMatch';
import { tempId } from '@/lib/tempId';
import { AttachMenu } from '@/components/contentPane/composer/AttachMenu';
import { FormatToolbar } from '@/components/contentPane/composer/FormatToolbar';
import { Tooltip } from '@/components/Tooltip';
import { SlashPopup } from '@/components/contentPane/composer/SlashPopup';
import { MentionPopup } from '@/components/contentPane/composer/MentionPopup';
import { ReplyPreviewBar } from '@/components/contentPane/composer/ReplyPreviewBar';
import { UploadPreviewStrip } from '@/components/contentPane/composer/UploadPreviewStrip';
import { RecordingHud } from '@/components/contentPane/composer/RecordingHud';
import { LivePreviewPanel } from '@/components/contentPane/composer/LivePreviewPanel';
import { useTypingEmitter } from '@/components/contentPane/composer/useTypingEmitter';
import { shouldAutofocusComposer } from '@/lib/composerAutofocus';
import {
  MicrophoneComposerIcon,
  SendIcon,
  SmileIcon,
} from '@/components/icons';
// EmojiPicker ships a giant inline unicode emoji table — lazy-load so
// the cost only appears when the user opens the picker.
const EmojiPicker = lazyWithReload(() =>
  import('@/components/EmojiPicker').then((m) => ({ default: m.EmojiPicker })),
);

export interface MentionCandidate {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export function Composer(props: {
  channelId: string;
  channelName: string;
  /** May I attach files (and voice notes) here? False: they are refused
   *  locally with the reason instead of failing after the upload. */
  canAttach?: boolean;
  replyingTo: Message | null;
  mentionCandidates: MentionCandidate[];
  onCancelReply: () => void;
  /** Put a reply target back after its send failed. */
  onRestoreReply?: (message: Message) => void;
  onSent: () => void;
}) {
  const { t } = useTranslation();
  // Hydrate composer text from the per-channel draft store. The Composer
  // remounts on channel switch (parent renders a fresh one per channelId),
  // so reading lazily in useState initializer gives us the right text
  // exactly once without a useEffect flash.
  const [draft, setDraft] = useState(() => getDraft(props.channelId));
  // Latest field value for callbacks that run outside render (hand-back).
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Sends go through the app-wide per-channel queue (composerQueue): a quick
  // second Enter queues behind the first, and order survives a remount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Nonce of a failed send handed back unchanged: resending it as is reuses
  // the nonce, so a send whose answer was lost cannot be duplicated.
  const onRestoreReplyRef = useRef(props.onRestoreReply);
  onRestoreReplyRef.current = props.onRestoreReply;
  const retryRef = useRef<{ content: string; uploadIds: string[]; nonce: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Markdown preview toggle — when on, a panel above the composer
  // renders the current draft through renderMarkdown so the user can
  // verify formatting before sending. Auto-hidden when the draft is
  // empty (nothing to preview).
  const [previewOn, setPreviewOn] = useState(false);
  // Format-toolbar visibility. Collapsed by default on every viewport so the
  // composer reads as one clean input; the user opts in via the "Aa" toggle
  // (desktop) or the + AttachMenu (mobile). `formatExplicit` is the manual
  // choice; null means "use the collapsed default". Composer remounts per
  // channel via key, so the choice resets cleanly when navigating away.
  const [formatExplicit, setFormatExplicit] = useState<boolean | null>(null);
  const formatOpen = formatExplicit ?? false;
  const setFormatOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    setFormatExplicit((cur) => {
      const prev = cur ?? false;
      return typeof next === 'function' ? next(prev) : next;
    });
  };
  // Poll creator modal — opened from the paperclip-neighbour "Опрос"
  // button. Lives in the composer (not at app-shell layer) because the
  // submit target is the active channelId.
  const [pollCreatorOpen, setPollCreatorOpen] = useState(false);
  const [taskCreatorOpen, setTaskCreatorOpen] = useState(false);
  // When ≥2 attachments are staged: group them into one message (default) or
  // send each as its own message.
  const [groupAttachments, setGroupAttachments] = useState(true);
  // GIF picker — opens from the "+ Прикрепить" menu. Selecting a GIF
  // turns it into an outbound attachment with mime image/gif so existing
  // bubble + S3-free image rendering surface it like any image upload.
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerRootRef = useRef<HTMLDivElement | null>(null);
  const {
    pendingUploads,
    startUploads: startAllowedUploads,
    cancelUpload,
    toggleAsFile,
    setAllAsFile,
    detach: detachUploads,
    restore: restoreUploads,
  } = useComposerUploads();
  const canAttach = props.canAttach !== false;
  function startUploads(files: FileList | File[]) {
    if (!canAttach) {
      toast.error(t('composer.attachNotAllowed'));
      return;
    }
    startAllowedUploads(files);
  }
  // Drag-and-drop state — true while a file is being dragged over the
  // composer. Used to render a dashed-accent overlay so the user knows
  // "drop here to attach". Discord-style affordance; matches the paste
  // handler below for keyboard-driven users (Ctrl+V screenshot).
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  // Latch: once the user opens the emoji picker for the first time, we
  // keep its DOM mounted forever (toggling visibility, not unmounting).
  // Preserves recent-emoji scroll position + skin-tone selection + search
  // input across reopens; first-open lazy-chunk cost paid only once.
  const emojiPickerEverOpened = useRef(false);
  if (pickerOpen) emojiPickerEverOpened.current = true;

  // Persist draft to localStorage (debounced 250ms inside the lib). Empty
  // string passes through to clear — keeps the DM-list indicator honest.
  // We use a layout-pace effect so the write fires synchronously with
  // the visible text update, not on the next paint.
  useEffect(() => {
    persistDraft(props.channelId, draft);
  }, [props.channelId, draft]);

  // Flush any pending debounced write when the user navigates away from
  // this channel, so the draft can't be lost to a quick tab-switch + close.
  useEffect(() => {
    const channelId = props.channelId;
    return () => {
      flushDraft(channelId);
    };
  }, [props.channelId]);

  // Mention autocomplete state. mentionQuery is null when the popup isn't
  // active; otherwise it holds the partial token (without the leading @),
  // the caret start (= where @ sits), and the highlighted candidate index.
  const [mention, setMention] = useState<
    { query: string; anchor: number; selected: number } | null
  >(null);
  // Slash-command popup state. Active when the draft starts with `/word`
  // (no whitespace yet), so the picker only competes with the textarea
  // while the user is mid-token. Disabled the moment they hit space —
  // that's when the body starts and the popup would just be noise.
  const [slash, setSlash] = useState<{ query: string; selected: number } | null>(null);

  // Opening a chat puts the caret in its message field, so the user can type
  // right away (mouse screens only, see shouldAutofocusComposer).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta || ta.disabled) return;
    if (shouldAutofocusComposer(document, window)) ta.focus({ preventScroll: true });
  }, [props.channelId]);

  // When user picks a reply target, focus the input so they can just type.
  useEffect(() => {
    if (props.replyingTo) taRef.current?.focus();
  }, [props.replyingTo]);

  // Grow for real content; an empty placeholder never makes the field taller.
  // Observe width/font changes as well as draft edits (zoom and mobile rotation).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    let raf = 0;
    let lastWidth = -1;
    let lastFont = '';
    function measure() {
      if (!ta) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const style = getComputedStyle(ta);
        lastWidth = ta.clientWidth;
        lastFont = style.fontSize;
        ta.style.height = '0px';
        const emptyHeight = parseFloat(style.lineHeight) + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        ta.style.height = `${Math.min(draft ? ta.scrollHeight : emptyHeight, 150)}px`;
      });
    }
    const observer = new ResizeObserver(() => {
      if (ta.clientWidth !== lastWidth || getComputedStyle(ta).fontSize !== lastFont) measure();
    });
    observer.observe(ta);
    measure();
    return () => { observer.disconnect(); cancelAnimationFrame(raf); };
  }, [draft]);

  // Filtered + ranked slash-command list, regenerated only when the
  // query token actually changes. Empty query → show all commands in
  // catalogue order; otherwise prefix-match first, then substring.
  const filteredSlash = useMemo(
    () => (slash ? rankSlashCommands(slash.query) : ([] as typeof SLASH_COMMANDS)),
    [slash],
  );

  /** Look at the draft head. If it's `/word` with no whitespace yet, open
   *  the slash popup with that partial. Closed otherwise. The popup is
   *  intentionally LEADING-ONLY (must start the message): there's no
   *  Discord-style mid-message slash chain, just a top-level command. */
  function syncSlashFromDraft(value: string) {
    const query = detectSlashQuery(value);
    if (query === null) {
      if (slash) setSlash(null);
      return;
    }
    setSlash((prev) =>
      prev && prev.query === query
        ? prev
        : {
            query,
            selected: prev ? Math.min(prev.selected, SLASH_COMMANDS.length - 1) : 0,
          },
    );
  }

  /** Replace the draft with the picked command label so the user can
   *  continue typing the body. We append a space when the command takes
   *  a body argument (label ends with `>`), so `/me ` is ready for the
   *  action text; arg-less commands like `/shrug` get no trailing space. */
  function applySlashPick(item: (typeof SLASH_COMMANDS)[number]) {
    const next = slashPickText(item.cmd, item.label.endsWith('>'));
    setDraft(next);
    setSlash(null);
    // Restore caret to end so typing continues the body.
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(next.length, next.length);
    });
  }

  const filteredMentions = useMemo(
    () => (mention ? rankMentions(props.mentionCandidates, mention.query) : []),
    [mention, props.mentionCandidates],
  );

  /** Look at the text BEFORE the caret. If the most recent token is `@…`
   * with no whitespace inside, open the mention popup with that partial. */
  function syncMentionFromCaret(value: string, caret: number) {
    const token = detectMentionQuery(value, caret);
    if (!token) {
      setMention(null);
      return;
    }
    setMention((prev) => ({
      query: token.query,
      anchor: token.anchor,
      selected: prev && prev.anchor === token.anchor ? Math.min(prev.selected, 7) : 0,
    }));
  }

  function applyMention(pick: MentionCandidate) {
    if (!mention) return;
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? draft.length;
    const { text: nextDraft, caret: nextCaret } = insertMention(
      draft,
      mention.anchor,
      caret,
      pick.username,
    );
    setDraft(nextDraft);
    setMention(null);
    // Restore caret to right after the inserted mention
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(nextCaret, nextCaret);
    });
  }

  async function send() {
    const rawContent = draft.trim();
    const ready = collectReadyAttachments(pendingUploads);
    const inFlight = hasUploadsInFlight(pendingUploads);
    if (!canSendMessage({ content: rawContent, readyCount: ready.length, inFlight })) {
      return;
    }

    // Intercept slash commands BEFORE the API call. /help is local-only
    // (shows a toast). /shrug, /me, /tableflip etc. expand the draft to
    // their substitution and continue as a normal message send.
    const me = useAuthStore.getState().user;
    const parsed = parseSlashCommand(rawContent, {
      displayName: me?.displayName ?? me?.username ?? null,
    });
    if (parsed?.consumeOnly) {
      if (parsed.notice) toast.message(parsed.notice);
      setDraft('');
      clearDraft(props.channelId);
      return;
    }
    const content = parsed?.expanded ?? rawContent;
    resetTyping();

    // Optimistic send — paint the bubble locally BEFORE the server roundtrip
    // so the user sees instant feedback (Discord's "feels fast" baseline).
    // Strategy:
    //   1. Build a temp Message with id = 'tmp-<uuid>'. Renderer detects the
    //      tmp- prefix and styles the bubble as pending (opacity dim).
    //   2. upsertMessage into the data store → bubble appears immediately.
    //   3. Clear composer state right away — user can type the next msg.
    //   4. Await api.sendMessage(); on success, swap tmp → real (the socket
    //      may also broadcast `message:new` with the real one; upsertMessage
    //      de-duplicates by id so the race is harmless).
    //   5. On failure, removeMessage(tmp), surface a toast and give the text
    //      and attachments back to the composer (giveBackFailed).
    //   Sends go through `sendQueue`, so Enter while a send is in flight
    //   queues the next message in order instead of being ignored.
    const author = {
      id: me?.id ?? '',
      platformId: me?.platformId ?? '',
      username: me?.username ?? '',
      displayName: me?.displayName ?? null,
      avatarUrl: me?.avatarUrl ?? null,
    };
    // Which uploads the user flagged "send as file" (server marks them so
    // images render as a download chip).
    const docIds = new Set(
      pendingUploads
        .filter((u) => u.status === 'done' && u.asFile && u.completed)
        .map((u) => u.completed!.id),
    );

    // Grouped (default) → one message with every attachment. Ungrouped with
    // ≥2 attachments → one message per attachment (caption + reply ride only
    // the first). One attachment or no attachments collapses to a single send.
    const batches = planSendBatches({ content, ready, grouped: groupAttachments });
    const replyingTo = props.replyingTo;
    const channelId = props.channelId;
    // Slots leave the tray now but keep their thumbnails until we know the
    // send landed — a failed send hands them back (U6).
    const sentSlots = pendingUploads;
    const grouped = groupAttachments;
    const retry = batches.length === 1
      ? retryNonce(retryRef.current, batches[0]!.content, batches[0]!.atts.map((a) => a.id))
      : null;
    retryRef.current = null;

    // Clear composer immediately — UX-wise this is the "send happened" moment.
    setDraft('');
    clearDraft(channelId);
    setMention(null);
    detachUploads();
    setGroupAttachments(true);
    props.onSent();

    // Paint every bubble now so a queued send shows up at once, then deliver
    // in order behind anything still waiting in this channel.
    const queued = batches.map((batch) => {
      const tmpId = tempId('tmp');
      useDataStore.getState().upsertMessage(
        buildOptimisticMessage({
          tmpId,
          channelId,
          content: batch.content,
          attachments: batch.atts,
          replyingTo: batch.withReply ? replyingTo : null,
          author,
        }),
      );
      return { ...batch, tmpId, nonce: retry ?? newNonce() };
    });

    await sendQueue.enqueue(channelId, {
      kind: 'message',
      batches: queued,
      slots: sentSlots,
      slotFor: (att) => sentSlots.find((slot) => slot.completed?.id === att.id),
      replyTo: replyingTo,
      grouped,
      send: (batch) => {
        const batchDocIds = batch.atts.filter((a) => docIds.has(a.id)).map((a) => a.id);
        return api.sendMessage(channelId, {
          ...buildSendPayload({
            content: batch.content,
            uploadIds: batch.atts.map((a) => a.id),
            replyToId: (batch.withReply ? replyingTo?.id : undefined) ?? null,
          }),
          ...(batchDocIds.length > 0 ? { documentUploadIds: batchDocIds } : {}),
          clientNonce: batch.nonce,
        });
      },
    });
  }

  /**
   * Take back failed sends of this channel — at mount (they may have failed
   * while another channel was open) and whenever one fails while mounted.
   * Text goes back in send order before anything typed since; attachments,
   * the reply and the grouping mode come back too.
   */
  useFailedSendHandBack(props.channelId, (items) => {
    const merged = mergeFailedParts(items);
    // Read the field outside the state updater: the updater must stay pure
    // (StrictMode runs it twice), so the nonce choice is made here.
    const cur = draftRef.current;
    // An unchanged single-batch resend may reuse its nonce (see retryNonce).
    const only = merged.batches.length === 1 && !cur.trim() ? merged.batches[0]! : null;
    retryRef.current = only
      ? { content: only.content, uploadIds: only.atts.map((a) => a.id), nonce: only.nonce }
      : null;
    const next = mergeRestoredDraft(cur, merged.content);
    draftRef.current = next;
    setDraft(next);
    restoreUploads(merged.slots);
    if (!merged.grouped) setGroupAttachments(false);
    if (merged.replyTo) onRestoreReplyRef.current?.(merged.replyTo);
  });

  const { emitTyping, resetTyping } = useTypingEmitter(props.channelId);

  /**
   * Wrap a finished recording in a File, push it through the uploader and
   * fire a one-attachment message. The hold-to-record hook handles capture,
   * cancel, and the too-short guard before calling this.
   */
  async function sendVoiceNote(result: RecordingResult) {
    if (!canAttach) {
      toast.error(t('composer.attachNotAllowed'));
      return;
    }
    const ext = result.mime.startsWith('audio/webm')
      ? 'webm'
      : result.mime.startsWith('audio/ogg')
        ? 'ogg'
        : 'audio';
    const filename = `voice-${Date.now()}.${ext}`;
    // Strip the `;codecs=opus` parameter — a clean `audio/webm` Content-Type
    // plays more reliably across browsers than the parameterised form.
    const cleanMime = result.mime.split(';')[0]!.trim() || result.mime;
    const file = new File([result.blob], filename, { type: cleanMime });
    const fileId = `${Date.now()}-voice`;
    const channelId = props.channelId;
    // Queued with text sends so a note recorded right after typing lands in order.
    await sendQueue.enqueue(channelId, { kind: 'task', run: async () => {
      try {
        const uploaded = await uploadFile(file, {
          fileId,
          durationMs: result.durationMs,
          onProgress: () => {
            /* tiny voice notes upload in <1s; no UI for the progress */
          },
        });
        uploaded.durationMs = result.durationMs;
        await api.sendMessage(channelId, {
          // Attachment-only message — blank body is allowed server-side now.
          content: ' ',
          uploadIds: [uploaded.id],
        });
        if (mountedRef.current) props.onSent();
      } catch (err) {
        toast.error(...errToToast(err, t('composer.voiceSendError')));
      }
    } });
  }

  // Telegram-style hold-to-record gesture (capture/cancel/too-short guard
  // live in the hook; sendVoiceNote does the upload + send).
  const { recorder, willCancel, handlers: holdHandlers } = useHoldToRecord({
    rootRef: composerRootRef,
    onResult: sendVoiceNote,
  });

  // Telegram-style: a non-empty draft or any staged attachment turns the
  // trailing mic into a Send button (mic stays while actively recording).
  const hasDraftToSend =
    recorder.kind !== 'recording' &&
    (draft.trim().length > 0 || pendingUploads.length > 0);

  /**
   * Apply a formatWrap helper to the current selection and restore focus +
   * caret. We commit the new value through setDraft so React re-renders;
   * then a requestAnimationFrame lets the textarea reach the new DOM
   * state before we set selection on it.
   */
  function applyFormat(fn: (s: string, a: number, b: number) => WrapResult) {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? start;
    const result = fn(draft, start, end);
    setDraft(result.value);
    requestAnimationFrame(() => {
      const node = taRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(result.selStart, result.selEnd);
    });
  }

  return (
    <div className="studio-composer px-4 pb-4 pt-2" ref={composerRootRef} data-bunker-window-busy={pendingUploads.length > 0 || recorder.kind === 'recording' || Boolean(props.replyingTo) || undefined}>
      <div className="chat-composer-extras scrollbar-thin">
      {props.replyingTo && (
        <ReplyPreviewBar replyingTo={props.replyingTo} onCancelReply={props.onCancelReply} />
      )}
      <UploadPreviewStrip
        uploads={pendingUploads}
        groupAttachments={groupAttachments}
        onSetAllAsFile={setAllAsFile}
        onToggleGroup={() => setGroupAttachments((v) => !v)}
        onToggleAsFile={toggleAsFile}
        onCancelUpload={cancelUpload}
      />
      {recorder.kind === 'recording' && (
        <RecordingHud durationMs={recorder.durationMs ?? 0} willCancel={willCancel} />
      )}
      {previewOn && draft.trim().length > 0 && <LivePreviewPanel draft={draft} />}
      {formatOpen && (
        <FormatToolbar
          onApply={applyFormat}
          previewOn={previewOn}
          onTogglePreview={() => setPreviewOn((v) => !v)}
        />
      )}
      </div>
      <div
        // `items-center` (was items-end) — with a single-row textarea
        // the icons sat at the bottom of the row and the placeholder
        // text floated above them, looking disconnected. Centering
        // the row aligns the icon optical centres with the text
        // baseline. On multi-line input the buttons still sit at the
        // vertical midpoint (Discord pattern) — frame stays balanced.
        className={
          'studio-compose-field relative flex items-center gap-2 px-4 py-2.5 transition-[background-color,border-color,color,box-shadow,opacity] ' +
          'rounded-xl bg-surface-secondary'
        }
        onDragEnter={(e) => {
          // Only show the overlay for file drags, not text/markdown drags
          // from within the page (those still get e.dataTransfer.types
          // populated but not with 'Files').
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          dragDepth.current += 1;
          if (!dragActive) setDragActive(true);
        }}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          // preventDefault required so onDrop can fire.
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragActive(false);
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          dragDepth.current = 0;
          setDragActive(false);
          const files = e.dataTransfer.files;
          if (files && files.length > 0) startUploads(files);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) startUploads(e.target.files);
            // reset so picking the same file twice still triggers a change
            e.target.value = '';
          }}
        />
        <AttachMenu
          onPickFile={() => fileInputRef.current?.click()}
          onCreatePoll={() => setPollCreatorOpen(true)}
          onCreateTasks={() => setTaskCreatorOpen(true)}
          // Mobile: emoji + format toggles live inside this menu so the
          // composer row keeps maximum textarea width.
          onOpenEmojiMobile={() => setPickerOpen(true)}
          formatOpenMobile={formatOpen}
          onToggleFormatMobile={() => setFormatOpen((v) => !v)}
        />
        <textarea
          ref={taRef}
          value={draft}
          onPaste={(e) => {
            // Discord-style: paste a screenshot from the clipboard → attaches
            // it as if you dragged it in. We only intercept when there's
            // actual image data; plain-text paste still goes to the textarea.
            const items = e.clipboardData?.items;
            if (!items) return;
            const files: File[] = [];
            for (let i = 0; i < items.length; i += 1) {
              const it = items[i];
              if (it && it.kind === 'file' && it.type.startsWith('image/')) {
                const f = it.getAsFile();
                if (f) files.push(f);
              }
            }
            if (files.length > 0) {
              e.preventDefault();
              startUploads(files);
            }
          }}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            emitTyping();
            syncMentionFromCaret(next, e.target.selectionStart ?? next.length);
            syncSlashFromDraft(next);
          }}
          onKeyUp={(e) => {
            // Caret moves on arrows/home/end without firing onChange — re-sync.
            if (
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' ||
              e.key === 'Home' ||
              e.key === 'End'
            ) {
              const t = e.currentTarget;
              syncMentionFromCaret(t.value, t.selectionStart ?? t.value.length);
            }
          }}
          onClick={(e) => {
            const t = e.currentTarget;
            syncMentionFromCaret(t.value, t.selectionStart ?? t.value.length);
          }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            // Slash-command popup keyboard nav comes BEFORE mention/send so
            // a draft like "/sh" handles ArrowDown without inserting newline
            // or sending a half-typed command.
            if (slash && filteredSlash.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSlash((prev) =>
                  prev
                    ? { ...prev, selected: (prev.selected + 1) % filteredSlash.length }
                    : prev,
                );
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSlash((prev) =>
                  prev
                    ? {
                        ...prev,
                        selected:
                          (prev.selected - 1 + filteredSlash.length) %
                          filteredSlash.length,
                      }
                    : prev,
                );
                return;
              }
              if (e.key === 'Tab' || (e.key === 'Enter' && !e.nativeEvent.isComposing)) {
                e.preventDefault();
                const pick = filteredSlash[slash.selected];
                if (pick) applySlashPick(pick);
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setSlash(null);
                return;
              }
            }
            // Mention popup keyboard nav takes priority over send / newline.
            if (mention && filteredMentions.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMention((prev) =>
                  prev
                    ? { ...prev, selected: (prev.selected + 1) % filteredMentions.length }
                    : prev,
                );
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMention((prev) =>
                  prev
                    ? {
                        ...prev,
                        selected:
                          (prev.selected - 1 + filteredMentions.length) %
                          filteredMentions.length,
                      }
                    : prev,
                );
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                if (!e.nativeEvent.isComposing) {
                  e.preventDefault();
                  const pick = filteredMentions[mention.selected];
                  if (pick) applyMention(pick);
                  return;
                }
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setMention(null);
                return;
              }
            }
            if (e.key === 'Escape' && props.replyingTo) {
              e.preventDefault(); props.onCancelReply(); return;
            }
            if (e.key === 'ArrowUp' && !draft && !pendingUploads.length && recorder.kind !== 'recording' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
              e.preventDefault(); emitAppEvent('chat:edit-last', { channelId: props.channelId }); return;
            }
            // Markdown formatting shortcuts. Use ctrlKey || metaKey so it
            // works on Windows/Linux (Ctrl) and macOS (⌘) without branching.
            // Cmd/Ctrl+B = bold, +I = italic, +U = underline, +Shift+X =
            // strike, +E = inline code, +Shift+. = spoiler (matches Discord),
            // +Shift+9 = quote, +K = link. Discord's set is the de-facto
            // standard for chat apps; copying it minimises muscle-memory
            // friction for users coming from there.
            if ((e.ctrlKey || e.metaKey) && !e.altKey) {
              const k = e.key.toLowerCase();
              const shift = e.shiftKey;
              const map: Record<string, () => void> = {
                b: () => applyFormat(wrapBold),
                i: () => applyFormat(wrapItalic),
                u: () => applyFormat(wrapUnderline),
                e: () => applyFormat(wrapCode),
              };
              if (shift) {
                if (k === 'k') { e.preventDefault(); applyFormat(wrapLink); return; }
                if (k === 'x') {
                  e.preventDefault();
                  applyFormat(wrapStrike);
                  return;
                }
                if (k === '.' || e.key === '>') {
                  e.preventDefault();
                  applyFormat(wrapSpoiler);
                  return;
                }
                if (k === '9' || e.key === '(') {
                  e.preventDefault();
                  applyFormat(wrapQuote);
                  return;
                }
              } else if (map[k]) {
                e.preventDefault();
                map[k]!();
                return;
              }
            }
            // `nativeEvent.isComposing` true while IME composition is active
            // (e.g. Russian input on macOS) — pressing Enter should commit the
            // composition, not send the message.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={
            props.replyingTo
              ? t('composer.replyPlaceholder', { name: props.replyingTo.author.displayName || props.replyingTo.author.username })
              : t('composer.writeMessage')
          }
          aria-label={t('composer.messagePlaceholder', { channel: props.channelName })}
          rows={1}
          maxLength={4000}
          className="scrollbar-thin flex-1 resize-none bg-transparent text-sm text-ink placeholder:text-ink-muted outline-none"
        />
        {/* Format-toolbar toggle ("Aa") — desktop only; opens the markdown
            bar on demand so the composer stays clean by default. Mobile gets
            the same toggle inside the + AttachMenu. */}
        <Tooltip label={t('composer.formatting')}>
          <Button
            isIconOnly
            size="md"
            variant="ghost"
            onPress={() => setFormatOpen((v) => !v)}
            aria-label={t('composer.formatting')}
            aria-pressed={formatOpen}
            className={'hidden shrink-0 md:flex ' + (formatOpen ? 'text-primary' : '')}
          >
            <span className="text-[15px] font-semibold leading-none">Aa</span>
          </Button>
        </Tooltip>
        {/* Standalone emoji button — desktop only. On mobile the entry
            lives inside the + AttachMenu so the composer-row keeps every
            spare pixel for the textarea. */}
        <Tooltip label={t('composer.emoji')}>
          <Button
            isIconOnly
            size="md"
            variant="ghost"
            onPress={() => setPickerOpen((v) => !v)}
            aria-label={t('composer.emoji')}
            className="hidden shrink-0 md:flex"
          >
            <SmileIcon className="h-[22px] w-[22px]" />
          </Button>
        </Tooltip>
        {/* Voice-message button — moved to the RIGHT edge of the composer
            (Telegram pattern). Last in the flex row so the trailing edge
            of the input strip is a discoverable single-tap action: empty
            draft + tap mic = record; recording + tap mic = stop + send.
            Red fill while recording so the active state reads at a glance.
            36×36 hit target meets Apple HIG / Material's minimum. */}
        {/* In a narrow panel the trailing action switches between recording
            and sending, leaving more width for the text. Desktop keeps both. */}
        <div className="studio-primary-action flex shrink-0">
        {/* Hold-to-record (Telegram pattern): press & hold to record, release
            to send, release with the cursor outside the composer to cancel. */}
        <button
          type="button"
          {...holdHandlers}
          title={t('composer.holdToRecord')}
          aria-label={t('composer.recordVoice')}
          className={
            (hasDraftToSend ? 'hidden md:grid ' : 'grid ') +
            'h-9 w-9 shrink-0 touch-none select-none place-items-center rounded-lg transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ' +
            (recorder.kind === 'recording'
              ? 'scale-110 bg-danger text-white'
              : 'text-ink-dim hover:bg-default/50 hover:text-foreground')
          }
        >
          <MicrophoneComposerIcon className="h-[22px] w-[22px]" />
        </button>
        <button
          type="button"
          onClick={() => void send()}
          disabled={!hasDraftToSend}
          aria-label={t('composer.send')}
          title={t('composer.send')}
          className={(hasDraftToSend ? 'grid ' : 'hidden md:grid ') + 'studio-send h-9 w-9 shrink-0 animate-[send-pop_0.18s_ease-out] place-items-center rounded-full bg-primary text-white shadow-sm transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60'}
        >
          <SendIcon className="h-[20px] w-[20px]" />
        </button>
        </div>
        {/* Emoji picker stays mounted once opened — toggling `hidden` instead
            of unmounting keeps recent-emoji scroll position, search input,
            and the unicode table init state across reopens. First-open
            cost is paid once per session, every reopen is instant. */}
        {emojiPickerEverOpened.current && (
          <div
            className={
              'absolute bottom-full right-2 mb-2 ' + (pickerOpen ? '' : 'hidden')
            }
            aria-hidden={!pickerOpen}
          >
            <Suspense fallback={null}>
              <EmojiPicker
                open={pickerOpen}
                onPick={(emoji) => {
                  setDraft((d) => d + emoji);
                  setPickerOpen(false);
                  taRef.current?.focus();
                }}
                onClose={() => setPickerOpen(false)}
              />
            </Suspense>
          </div>
        )}
        {mention && filteredMentions.length > 0 && (
          <MentionPopup
            candidates={filteredMentions}
            selected={mention.selected}
            onPick={applyMention}
            onHover={(idx) =>
              setMention((prev) => (prev ? { ...prev, selected: idx } : prev))
            }
          />
        )}
        {slash && filteredSlash.length > 0 && (
          <SlashPopup
            commands={filteredSlash}
            selected={slash.selected}
            onPick={applySlashPick}
            onHover={(idx) =>
              setSlash((prev) => (prev ? { ...prev, selected: idx } : prev))
            }
          />
        )}
        {dragActive && (
          // Drop-zone overlay — dashed cyan ring + faint accent wash. Sits
          // on top of the composer surface so the cursor still visually
          // "owns" the drop target. pointer-events-none so it doesn't
          // swallow the drop event itself (the wrapper div handles that).
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl"
            style={{
              backgroundColor: 'rgb(var(--bk-accent) / 0.08)',
              boxShadow: 'inset 0 0 0 2px rgb(var(--bk-accent) / 0.6)',
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent, transparent 8px, rgb(var(--bk-accent) / 0.06) 8px, rgb(var(--bk-accent) / 0.06) 16px)',
            }}
          >
            <span className="font-medium text-[11px] text-accent">
              {t('composer.releaseToAttach')}
            </span>
          </div>
        )}
      </div>
      <PollCreatorModal
        open={pollCreatorOpen}
        channelId={props.channelId}
        onClose={() => setPollCreatorOpen(false)}
      />
      <TaskCreatorModal
        open={taskCreatorOpen}
        channelId={props.channelId}
        onClose={() => setTaskCreatorOpen(false)}
      />
    </div>
  );
}
